import { z, uuidSchema } from '@kc3/validation';
import { PortalVisibilitySchema } from '../enums/index.js';
const PORTAL_TEMPLATE_TYPES = ['home', 'footer', 'navbar'];
const PORTAL_TEMPLATE_STATUSES = ['draft', 'published'];
const PORTAL_CONFIG_STATUSES = ['active', 'inactive'];
export const PortalTemplateSectionSchema = z.object({
    sectionType: z.string(),
    visible: z.boolean().default(true),
    order: z.number().int().min(0),
    settings: z.record(z.string(), z.unknown()).default({}),
});
export const PortalTemplateSchema = z.object({
    id: uuidSchema,
    tenantId: uuidSchema,
    type: z.enum(PORTAL_TEMPLATE_TYPES),
    name: z.string(),
    sections: z.array(PortalTemplateSectionSchema),
    status: z.enum(PORTAL_TEMPLATE_STATUSES),
    createdAt: z.string().datetime(),
    updatedAt: z.string().datetime(),
    updatedBy: uuidSchema.nullable(),
});
export const CreatePortalTemplateRequestSchema = z.object({
    type: z.enum(PORTAL_TEMPLATE_TYPES),
    name: z.string().min(1),
    sections: z.array(PortalTemplateSectionSchema).default([]),
    status: z.enum(PORTAL_TEMPLATE_STATUSES).default('draft'),
});
export const UpdatePortalTemplateRequestSchema = z
    .object({
    name: z.string().min(1).optional(),
    sections: z.array(PortalTemplateSectionSchema).optional(),
    status: z.enum(PORTAL_TEMPLATE_STATUSES).optional(),
})
    .refine((v) => Object.values(v).some((x) => x !== undefined), {
    message: 'At least one field must be provided',
});
export const PortalTemplateListQuerySchema = z.object({
    type: z.enum(PORTAL_TEMPLATE_TYPES).optional(),
    status: z.enum(PORTAL_TEMPLATE_STATUSES).optional(),
    search: z.string().trim().min(1).optional(),
});
// ---------- Portal Configuration — 5 sections ----------
const QUICK_COLOR_PRESETS = ['white', 'light_gray', 'black', 'blue', 'green', 'red', 'yellow', 'purple'];
export const PortalBackgroundSchema = z.object({
    imageUrl: z.string().nullable().default(null),
    color: z.string().default('#FFFFFF'),
    opacity: z.number().min(0).max(1).default(1),
    preset: z.enum(QUICK_COLOR_PRESETS).nullable().default(null),
});
export const FONT_OPTIONS = [
    'Inter',
    'Fraunces',
    'JetBrains Mono',
    'Arial',
    'Helvetica',
    'Georgia',
    'Times New Roman',
    'Verdana',
    'Tahoma',
    'Segoe UI',
    'Roboto',
    'system-ui',
];
export const GoogleFontEntrySchema = z.object({
    family: z.string(),
    category: z.string(),
    variants: z.array(z.string()),
});
export const GoogleFontsCatalogResponseSchema = z.object({
    source: z.enum(['google', 'fallback']),
    fonts: z.array(GoogleFontEntrySchema),
});
export const PortalDesignConfigSchema = z.object({
    templateMode: z.enum(['starter', 'custom']).default('starter'),
    headerTitle: z.string().default(''),
    welcomeTitle: z.string().default(''),
    welcomeMessage: z.string().default(''),
    theme: z.string().default('blue'),
    buttonTextColor: z.string().default('#ffffff'),
    ticketButtonLabel: z.string().default('Raise a Conversation'),
    layout: z.string().default('menu_top'),
    homeDesign: uuidSchema.nullable().default(null),
    footerDesign: uuidSchema.nullable().default(null),
    navbarDesign: uuidSchema.nullable().default(null),
    font: z.string().min(1).default('Inter'),
    background: PortalBackgroundSchema.default({}),
    logoUrl: z.string().nullable().default(null),
    faviconUrl: z.string().nullable().default(null),
    bannerUrl: z.string().nullable().default(null),
    footerBackgroundColor: z.string().default(''),
    footerTextColor: z.string().default('#6b7280'),
    companyName: z.string().default(''),
    contactEmail: z.string().default(''),
    companyAddress: z.string().default(''),
    contactPhone: z.string().default(''),
    facebookUrl: z.string().default(''),
    instagramUrl: z.string().default(''),
    linkedinUrl: z.string().default(''),
    twitterUrl: z.string().default(''),
    youtubeUrl: z.string().default(''),
});
const PortalModuleVisibilitySchema = z.object({
    visibility: PortalVisibilitySchema.default('logged_in_user'),
});
export const PortalAccessPermissionsConfigSchema = z.object({
    news: PortalModuleVisibilitySchema.default({}),
    knowledgebase: PortalModuleVisibilitySchema.default({}),
    faq: PortalModuleVisibilitySchema.default({}),
    announcements: PortalModuleVisibilitySchema.default({}),
    chat: z.object({
        enabled: z.boolean().default(true),
        botMode: z.enum(['faq_auto', 'decision_tree']).default('faq_auto'),
    }).default({}),
    call: z.object({
        enabled: z.boolean().default(false),
        queueId: uuidSchema.optional(),
    }).default({}),
    googleSignin: z.object({
        enabled: z.boolean().default(false),
        clientId: z.string().default(''),
        clientSecret: z.string().default(''),
    }).default({}),
    microsoftSignin: z.object({
        enabled: z.boolean().default(false),
        clientId: z.string().default(''),
        clientSecret: z.string().default(''),
    }).default({}),
    facebookSignin: z.object({
        enabled: z.boolean().default(false),
        appId: z.string().default(''),
        appSecret: z.string().default(''),
    }).default({}),
    approvalLogin: z.boolean().default(false),
    forgotPassword: z.boolean().default(true),
});
const PORTAL_TICKET_ACCESS = ['everyone', 'logged_in_user'];
export const PortalClientPermissionsConfigSchema = z.object({
    downloadAttachments: z.boolean().default(true),
    deleteAttachments: z.boolean().default(false),
    attachmentViewNewTab: z.boolean().default(true),
    submitTicket: z.object({ who: z.enum(PORTAL_TICKET_ACCESS).default('everyone') }).default({}),
    viewTicket: z.object({ who: z.enum(PORTAL_TICKET_ACCESS).default('logged_in_user') }).default({}),
});
export const TicketFormFieldsConfigSchema = z.object({
    showConversationForm: z.boolean().default(true),
    showConsumerType: z.boolean().default(true),
    showSubject: z.boolean().default(true),
    showDescription: z.boolean().default(true),
    showAttachments: z.boolean().default(true),
    showLocation: z.boolean().default(true),
});
export const PortalTicketRulesConfigSchema = z.object({
    reopenDays: z.number().int().min(0).default(7),
    reopenMessage: z.string().default(''),
    disclaimer: z.object({ enabled: z.boolean().default(false), message: z.string().default('') }).default({}),
    ticketFormId: uuidSchema.nullable().default(null),
    privacyPolicy: z.string().default(''),
    formFields: TicketFormFieldsConfigSchema.default({}),
});
export const PortalRatingConfigSchema = z.object({
    perReplyEnabled: z.boolean().default(false),
    perInteractionEnabled: z.boolean().default(true),
    ratingConfig: z.object({
        scale: z.number().int().min(2).max(10).default(5),
        labels: z.record(z.string(), z.string()).default({}),
    }).default({}),
});
export const PortalChatWidgetConfigSchema = z.object({
    enabled: z.boolean().default(false),
    allowedDomains: z.array(z.string().trim().toLowerCase()).default([]),
    position: z.enum(['bottom_right', 'bottom_left']).default('bottom_right'),
    offsetBottom: z.number().int().min(0).default(24),
    offsetRight: z.number().int().min(0).default(24),
    iconSize: z.number().int().min(32).max(96).default(56),
    primaryColor: z.string().default('#3454cf'),
    welcomeMessage: z.string().default('Hi there! How can we help you today?'),
    showUnreadCount: z.boolean().default(true),
});
export const PortalFormsConfigSchema = z.object({
    /** Ordered ids of portal_forms shown as navigation items on the client portal. */
    selectedFormIds: z.array(uuidSchema).default([]),
});
/**
 * Client-portal "Talk to our AI assistant" button: a browser voice call
 * answered by one of the tenant's AI voice agents (admin AI Center →
 * Voice Agents). Hidden unless enabled AND the agent is published.
 */
export const PortalVoiceAssistantConfigSchema = z.object({
    enabled: z.boolean().default(false),
    agentId: uuidSchema.nullable().default(null),
    label: z.string().trim().min(1).max(40).default('Talk to our AI assistant'),
    position: z.enum(['bottom_right', 'bottom_left']).default('bottom_left'),
    color: z.string().regex(/^#[0-9a-fA-F]{6}$/).default('#3366FF'),
    /** Only signed-in clients see the button (their tickets become available to the AI). */
    requireLogin: z.boolean().default(false),
});
/**
 * PV-16: tenant-supplied privacy notice for the portal and chat widget.
 * Everything here is supplied by the tenant (no default wording is shipped);
 * disabled by default, in which case nothing is shown or recorded.
 * `version` is recorded with each acceptance, so bump it when the notice changes.
 */
const httpUrl = z
    .string()
    .trim()
    .max(2000)
    .refine((v) => v === '' || /^https?:\/\/[^\s<>"']+$/i.test(v), { message: 'Must be an http(s) URL' });
export const PortalPrivacyNoticeConfigSchema = z.object({
    enabled: z.boolean().default(false),
    noticeUrl: httpUrl.default(''),
    noticeText: z.string().trim().max(1000).default(''),
    version: z.string().trim().max(64).default(''),
    /** SW-RM8: when > 0 the chat widget asks the visitor to confirm they are at least this age. */
    minimumAge: z.number().int().min(0).max(21).default(0),
    /** SW-RM13: per-language notice, keyed by lowercase BCP-47 language ("fr", "pt-br"). Falls back to noticeText/noticeUrl. */
    translations: z
        .record(z.string().regex(/^[a-z]{2,3}(-[a-z0-9]{2,8})?$/), z.object({ noticeText: z.string().trim().max(1000).default(''), noticeUrl: httpUrl.default('') }))
        .default({}),
});
/** The notice is only active when switched on AND it has a version and something to show. */
export function isPrivacyNoticeActive(c) {
    return !!c && c.enabled && c.version !== '' && (c.noticeUrl !== '' || c.noticeText !== '');
}
/** The notice as the chat widget receives it (SW-RM8 minimum age, SW-RM13 translations). */
export function toWidgetPrivacyNotice(n) {
    return {
        url: n.noticeUrl,
        text: n.noticeText,
        version: n.version,
        ...(n.minimumAge > 0 && { minimumAge: n.minimumAge }),
        ...(Object.keys(n.translations).length > 0 && {
            translations: Object.fromEntries(Object.entries(n.translations).map(([k, v]) => [k, { text: v.noticeText, url: v.noticeUrl }])),
        }),
    };
}
export const PortalConfigDataSchema = z.object({
    design: PortalDesignConfigSchema.default({}),
    accessPermissions: PortalAccessPermissionsConfigSchema.default({}),
    clientPermissions: PortalClientPermissionsConfigSchema.default({}),
    ticketRules: PortalTicketRulesConfigSchema.default({}),
    rating: PortalRatingConfigSchema.default({}),
    chatWidget: PortalChatWidgetConfigSchema.default({}),
    forms: PortalFormsConfigSchema.default({}),
    voiceAssistant: PortalVoiceAssistantConfigSchema.default({}),
    privacyNotice: PortalPrivacyNoticeConfigSchema.default({}),
});
const PORTAL_CONFIG_SECTIONS = ['design', 'accessPermissions', 'clientPermissions', 'ticketRules', 'rating', 'chatWidget', 'forms', 'voiceAssistant', 'privacyNotice'];
export const PortalConfigurationSchema = z.object({
    id: uuidSchema,
    tenantId: uuidSchema,
    status: z.enum(PORTAL_CONFIG_STATUSES),
    draftConfig: PortalConfigDataSchema,
    publishedConfig: PortalConfigDataSchema,
    publishedAt: z.string().datetime().nullable(),
    publishedBy: uuidSchema.nullable(),
    updatedAt: z.string().datetime(),
    updatedBy: uuidSchema.nullable(),
});
// PATCH .../draft body: { section, values } — merges `values` into just that
// section of draft_config, leaving the other 4 sections untouched.
export const UpdatePortalConfigurationDraftRequestSchema = z.object({
    section: z.enum(PORTAL_CONFIG_SECTIONS),
    values: z.record(z.string(), z.unknown()),
});
// POST .../publish takes no body — it always publishes the server's current
// draft_config, never an arbitrary client-supplied config.
export const PublishPortalConfigurationResponseSchema = z.object({
    publishedConfig: PortalConfigDataSchema,
    publishedAt: z.string().datetime(),
    publishedBy: uuidSchema.nullable(),
});
// GET /api/portal-config/live — public, tenant-scoped, published_config only.
export const PortalConfigLiveResponseSchema = z.object({
    status: z.enum(PORTAL_CONFIG_STATUSES),
    config: PortalConfigDataSchema,
});
const PORTAL_ASSET_KINDS = ['logo', 'favicon', 'background', 'banner', 'navbar_logo'];
export const PortalAssetKindSchema = z.enum(PORTAL_ASSET_KINDS);
export const PortalAssetUploadResponseSchema = z.object({
    kind: PortalAssetKindSchema,
    url: z.string(),
});
//# sourceMappingURL=portal-config.js.map